import React, { useState } from 'react';

import { AppButton, AppModal } from '@/components/atoms';
import useAppToast from '@/hooks/useAppToast';
import { outputConsentFormPdf } from '@/pages/main/consent/services/print';
import { getApiErrorMessage } from '@/utils/apiError';

type ChoiceType = 'a' | 'b' | 'ab';

interface ConsentFormPrintModalProps {
  consentId: number;
  siteType: string;
  clinicNumber: string;
  serviceTypeHeader: string;
  isOpen: boolean;
  onClose: () => void;
}

const ConsentFormPrintModal: React.FC<ConsentFormPrintModalProps> = ({
  consentId,
  siteType,
  clinicNumber,
  serviceTypeHeader,
  isOpen,
  onClose,
}) => {
  const toast = useAppToast();
  const [loadingChoice, setLoadingChoice] = useState<ChoiceType | null>(null);

  const runPrint = async (choiceType: ChoiceType) => {
    setLoadingChoice(choiceType);
    // Open the tab synchronously while still in the user-gesture context so popup
    // blockers don't drop it after the await, and so noopener-locked blob URLs
    // don't surface as cross-origin errors. URL is set after the PDF arrives.
    const pdfWindow = window.open('about:blank', '_blank');
    try {
      const blob = await outputConsentFormPdf(
        siteType,
        clinicNumber,
        consentId,
        choiceType,
        serviceTypeHeader
      );
      const url = URL.createObjectURL(blob);
      if (pdfWindow) {
        pdfWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      if (pdfWindow) pdfWindow.close();
      toast.error(getApiErrorMessage(e, 'PDFの生成に失敗しました。'));
    } finally {
      setLoadingChoice(null);
    }
  };

  const isLoading = loadingChoice !== null;

  return (
    <AppModal isOpen={isOpen} onClose={onClose} title="同意書印刷" maxWidth={560} footer={null}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-12 items-start">
          <AppButton
            type="primary"
            onClick={() => runPrint('a')}
            loading={loadingChoice === 'a'}
            disabled={isLoading && loadingChoice !== 'a'}
          >
            　表面印刷　
          </AppButton>
          <div className="flex flex-col gap-1">
            <AppButton
              type="primary"
              onClick={() => runPrint('ab')}
              className="w-fit"
              loading={loadingChoice === 'ab'}
              disabled={isLoading && loadingChoice !== 'ab'}
            >
              　両面印刷　
            </AppButton>
            <span className="text-sm text-gray-600">両面印刷対応プリンタをご利用の方</span>
          </div>
        </div>
        <div>
          <AppButton
            type="primary"
            onClick={() => runPrint('b')}
            loading={loadingChoice === 'b'}
            disabled={isLoading && loadingChoice !== 'b'}
          >
            　裏面印刷　
          </AppButton>
        </div>
      </div>
    </AppModal>
  );
};

export default ConsentFormPrintModal;
